
import React, { useRef, useState } from 'react';
import { Download, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReadingPlanDay } from "@/hooks/useReadingPlan";
import jsPDF from "jspdf";
import ReadingPlanDaysViewer from "./ReadingPlanDaysViewer";

interface ReadingPlanResultProps {
  readingPlan: { readingPlan: ReadingPlanDay[] };
  topic: string;
  onSave: () => void;
  isSaving: boolean;
}

const ReadingPlanResult: React.FC<ReadingPlanResultProps> = ({ 
  readingPlan, 
  topic,
  onSave,
  isSaving
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
      });
      const titleFontSize = 22;
      const headingFontSize = 16;
      const textFontSize = 12;
      pdf.setFontSize(titleFontSize);
      pdf.text(`Reading Plan: ${topic}`, 40, 40);
      let yOffset = 70;
      const pageWidth = pdf.internal.pageSize.getWidth() - 80;

      for (const day of readingPlan.readingPlan) {
        if (yOffset > pdf.internal.pageSize.getHeight() - 40) {
          pdf.addPage();
          yOffset = 40;
        }
        pdf.setFontSize(headingFontSize);
        pdf.text(`${day.day} - ${day.passage}`, 40, yOffset);
        yOffset += 20;
        if (day.scripture) {
          pdf.setFontSize(textFontSize);
          const scriptureText = `Scripture: ${day.scripture}`;
          const scriptureLines = pdf.splitTextToSize(scriptureText, pageWidth);
          pdf.text(scriptureLines, 40, yOffset);
          yOffset += (scriptureLines.length * 14);
        }
        pdf.setFontSize(textFontSize);
        const commentaryLines = pdf.splitTextToSize(day.commentary, pageWidth);
        pdf.text(commentaryLines, 40, yOffset);
        yOffset += (commentaryLines.length * 14) + 10;

        pdf.setFontSize(headingFontSize - 2);
        pdf.text("Action Steps:", 40, yOffset);
        yOffset += 20;
        pdf.setFontSize(textFontSize);
        for (const step of day.actionSteps) {
          const stepLines = pdf.splitTextToSize(`• ${step}`, pageWidth);
          pdf.text(stepLines, 40, yOffset);
          yOffset += (stepLines.length * 14) + 5;
          if (yOffset > pdf.internal.pageSize.getHeight() - 60) {
            pdf.addPage();
            yOffset = 40;
          }
        }

        pdf.setFontSize(headingFontSize - 2);
        pdf.text("Prayer Points:", 40, yOffset);
        yOffset += 20;
        pdf.setFontSize(textFontSize);
        for (const prayer of day.prayerPoints) {
          const prayerLines = pdf.splitTextToSize(`• ${prayer}`, pageWidth);
          pdf.text(prayerLines, 40, yOffset);
          yOffset += (prayerLines.length * 14) + 5;
          if (yOffset > pdf.internal.pageSize.getHeight() - 60) {
            pdf.addPage();
            yOffset = 40;
          }
        }
        yOffset += 30;
        if (yOffset > pdf.internal.pageSize.getHeight() - 100) {
          pdf.addPage();
          yOffset = 40;
        }
      }
      pdf.save(`reading_plan_${topic.replace(/\s+/g, '_').toLowerCase()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div>
      <div className="flex gap-2 justify-end mb-3">
        <Button
          onClick={handleDownloadPdf}
          variant="outline"
          className="border-heaven-gold text-heaven-gold hover:bg-heaven-gold/10"
          disabled={isDownloading}
        >
          {isDownloading ? (
            <>Downloading...</>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </>
          )}
        </Button>
        <Button 
          onClick={onSave}
          disabled={isSaving}
          className="bg-heaven-gold hover:bg-heaven-dark-gold text-white"
        >
          {isSaving ? (
            <>Saving...</>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save to Dashboard
            </>
          )}
        </Button>
      </div>
      <ReadingPlanDaysViewer 
        readingPlan={readingPlan}
        topic={topic}
      />
    </div>
  );
};

export default ReadingPlanResult;

